import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Upload, FileText } from "lucide-react";
import { api } from "@/api/client";
import { LAYER_COLORS, ELEMENT_TYPES } from "@/constants/archimate";
import { toast } from "sonner";

export function ImportPanel({ projectId, onComplete, defaultTab }) {
  const [tab, setTab] = useState(defaultTab || "archimate");
  const [archiFile, setArchiFile] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [docText, setDocText] = useState("");
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [selected, setSelected] = useState({});

  const importArchimate = async () => {
    if (!archiFile) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", archiFile);
    try {
      const res = await api.post(`/projects/${projectId}/import/archimate`, fd);
      toast.success(`Imported ${res.data.elements_count} elements, ${res.data.relationships_count} relationships`);
      onComplete(res.data.view_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const extractDocument = async () => {
    if (!docFile && !docText.trim()) return;
    setLoading(true);
    const fd = new FormData();
    if (docFile) fd.append("file", docFile);
    if (docText.trim()) fd.append("text", docText.trim());
    try {
      const res = await api.post(`/projects/${projectId}/import/document/extract`, fd);
      setDraft(res.data);
      const sel = {};
      res.data.elements.forEach((_, i) => (sel[`e${i}`] = true));
      res.data.relationships.forEach((_, i) => (sel[`r${i}`] = true));
      setSelected(sel);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI extraction failed");
    } finally {
      setLoading(false);
    }
  };

  const confirmImport = async () => {
    setLoading(true);
    const elements = draft.elements.filter((_, i) => selected[`e${i}`]);
    const selectedNames = new Set(elements.map((e) => e.name));
    const relationships = draft.relationships.filter(
      (r, i) => selected[`r${i}`] && selectedNames.has(r.source_name) && selectedNames.has(r.target_name)
    );
    try {
      const res = await api.post(`/projects/${projectId}/import/document/confirm`, { elements, relationships });
      toast.success(`Created ${res.data.elements_count} elements, ${res.data.relationships_count} relationships`);
      onComplete(res.data.view_id);
    } catch (e) {
      toast.error("Could not commit the draft model");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="import-panel">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="archimate" data-testid="import-tab-archimate">
            Upload .archimate File
          </TabsTrigger>
          <TabsTrigger value="document" data-testid="import-tab-document">
            Document Extraction
          </TabsTrigger>
        </TabsList>
        <TabsContent value="archimate" className="space-y-3 pt-2">
          <p className="text-xs text-muted-foreground">
            Upload a native Archi <code>.archimate</code> exchange file. Elements, relationships and views will be read into this project.
          </p>
          <Input data-testid="archimate-file-input" type="file" accept=".archimate,.xml" onChange={(e) => setArchiFile(e.target.files[0])} />
          <Button data-testid="archimate-import-submit-button" disabled={!archiFile || loading} onClick={importArchimate}>
            {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : <Upload size={14} className="mr-2" />} Import File
          </Button>
        </TabsContent>
        <TabsContent value="document" className="space-y-3 pt-2">
          {!draft && (
            <>
              <p className="text-xs text-muted-foreground">
                Paste a description of your systems/processes, or upload a .txt, .pdf or .docx file. AI will draft a starting model for you to
                review.
              </p>
              <Textarea
                data-testid="document-text-input"
                value={docText}
                onChange={(e) => setDocText(e.target.value)}
                placeholder="Paste text describing your architecture..."
                rows={5}
              />
              <Input data-testid="document-file-input" type="file" accept=".txt,.pdf,.docx" onChange={(e) => setDocFile(e.target.files[0])} />
              <Button
                data-testid="document-extract-button"
                disabled={loading || (!docFile && !docText.trim())}
                onClick={extractDocument}
              >
                {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : <FileText size={14} className="mr-2" />} Build Architecture
              </Button>
            </>
          )}
          {draft && (
            <div className="space-y-3">
              <div>
                <div className="text-xs font-semibold mb-1">Elements ({draft.elements.length})</div>
                <div className="max-h-56 overflow-y-auto border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead></TableHead>
                        <TableHead>Layer</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {draft.elements.map((e, i) => {
                        const info = ELEMENT_TYPES[e.type];
                        const colors = LAYER_COLORS[info?.layer] || LAYER_COLORS.business;
                        return (
                          <TableRow key={i} data-testid={`draft-element-row-${i}`}>
                            <TableCell>
                              <Checkbox
                                checked={!!selected[`e${i}`]}
                                onCheckedChange={(v) => setSelected((s) => ({ ...s, [`e${i}`]: v }))}
                                data-testid={`draft-element-checkbox-${i}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Badge className={colors.badge} variant="outline">
                                {info?.label || e.type}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">{e.name}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{e.type}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold mb-1">Relationships ({draft.relationships.length})</div>
                <div className="max-h-40 overflow-y-auto border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead></TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Target</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {draft.relationships.map((r, i) => (
                        <TableRow key={i} data-testid={`draft-relationship-row-${i}`}>
                          <TableCell>
                            <Checkbox
                              checked={!!selected[`r${i}`]}
                              onCheckedChange={(v) => setSelected((s) => ({ ...s, [`r${i}`]: v }))}
                              data-testid={`draft-relationship-checkbox-${i}`}
                            />
                          </TableCell>
                          <TableCell className="text-sm">{r.source_name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{r.type}</TableCell>
                          <TableCell className="text-sm">{r.target_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDraft(null)} data-testid="document-draft-back-button">
                  Back
                </Button>
                <Button onClick={confirmImport} disabled={loading} data-testid="document-import-confirm-button">
                  {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : null} Import Selected
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
